import React from 'react';
import { Users, Award, MapPin, Target, Eye, Heart, CheckCircle } from 'lucide-react';

const About = () => {
  const stats = [
    { 
      icon: Users, 
      label: 'Years of Experience', 
      value: '10+', 
      description: 'Serving Qatar with excellence',
      color: 'from-blue-500 to-blue-600'
    },
    { 
      icon: Award, 
      label: 'Projects Completed', 
      value: '500+', 
      description: 'Successful installations & repairs',
      color: 'from-purple-500 to-purple-600'
    },
    { 
      icon: MapPin, 
      label: 'Locations Served', 
      value: 'Qatar-wide', 
      description: 'From Doha to Al Rayyan',
      color: 'from-green-500 to-green-600'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Precision',
      description: 'Every installation and repair is executed with meticulous attention to detail, ensuring optimal performance and longevity.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Award,
      title: 'Performance',
      description: 'We deliver high-performance solutions that exceed expectations, keeping your spaces comfortable year-round.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Heart,
      title: 'Perfection',
      description: 'Our commitment to perfection drives us to continuously improve and provide the best possible service experience.',
      color: 'from-green-500 to-green-600'
    }
  ];

  const achievements = [
    'Licensed & Certified Technicians',
    'Qatar Ministry Approved',
    '24/7 Emergency Response',
    'ISO Quality Standards',
    'Customer Satisfaction Guarantee',
    'Warranty on All Services'
  ];

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-purple-50 rounded-full px-4 py-2 border border-purple-200 mb-6">
              <span className="text-purple-700 font-medium text-sm">About Our Company</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Nebula Trading and Technical Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your trusted partner in air conditioning and refrigeration services across Qatar, 
              delivering professional excellence since our establishment.
            </p>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
                <div className={`bg-gradient-to-r ${stat.color} w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <stat.icon size={32} className="text-white" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-lg font-semibold text-purple-600 mb-2">{stat.label}</div>
                <div className="text-gray-600 text-sm">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Our Story & Mission
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Nebula Trading & Technical Services was founded with a simple mission: to provide 
                  reliable, professional air conditioning and refrigeration services across Qatar. 
                  Located in Muaither, Doha, we have grown from a small local business to a trusted 
                  name in the HVAC industry.
                </p>
                <p>
                  Our team of skilled technicians brings years of hands-on experience to every project, 
                  whether it's a simple residential AC installation or a complex commercial cooling system. 
                  We understand the unique challenges of Qatar's climate and are committed to keeping your 
                  spaces comfortable year-round.
                </p>
                <p>
                  What sets us apart is our comprehensive approach to service. We don't just install and 
                  repair – we build lasting relationships with our clients through reliable maintenance, 
                  quality spare parts supply, and 24/7 emergency support.
                </p>
              </div>

              {/* Achievements */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Our Certifications & Achievements</h3>
                <div className="grid grid-cols-2 gap-3">
                  {achievements.map((achievement, idx) => (
                    <div key={idx} className="flex items-center">
                      <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700 text-sm font-medium">{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                <img
                  src="https://images.unsplash.com/photo-1642749776312-aa42ce20c9f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxBQyUyMHJlcGFpcnxlbnwwfHx8fDE3NTMzODEwODd8MA&ixlib=rb-4.1.0&q=85"
                  alt="AC Technicians at Work"
                  className="w-full h-80 object-cover rounded-xl"
                />
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-xl shadow-xl">
                <div className="text-center">
                  <div className="text-2xl font-bold">24/7</div>
                  <div className="text-sm">Emergency Service</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Three fundamental principles that guide everything we do and define our commitment to excellence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 border border-gray-200 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className={`bg-gradient-to-r ${value.color} w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <value.icon size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 text-white">
              <div className="flex items-center mb-6">
                <div className="bg-white/20 p-3 rounded-xl mr-4">
                  <Target size={24} />
                </div>
                <h3 className="text-2xl font-bold">Our Mission</h3>
              </div>
              <p className="leading-relaxed opacity-90">
                To provide reliable, efficient, and affordable air conditioning and refrigeration 
                services that exceed our customers' expectations. We are committed to maintaining 
                the highest standards of quality and professionalism in every interaction.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 text-white">
              <div className="flex items-center mb-6">
                <div className="bg-white/20 p-3 rounded-xl mr-4">
                  <Eye size={24} />
                </div>
                <h3 className="text-2xl font-bold">Our Vision</h3>
              </div>
              <p className="leading-relaxed opacity-90">
                To be Qatar's leading air conditioning and refrigeration service provider, 
                recognized for our technical excellence, customer satisfaction, and innovative 
                solutions that contribute to a more comfortable and sustainable future.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;